import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

type Primitives = string | number | boolean | null | undefined

/**
 * Hook genérico: sincroniza search params da URL com estado local.
 * Tipos dos valores são inferidos automaticamente a partir dos defaults.
 *
 * @param defaults - Valores padrão. As chaves definem quais params monitorar.
 * @param options.replace - Usa history.replace em vez de push (padrão: true)
 *
 * @example
 * // Paginação
 * const [{ page, perPage }, setState] = useUrlState({ page: 1, perPage: 25 })
 *
 * // Estado de seleção compartilhável
 * const [{ companyId, competencia }, setState] = useUrlState({
 *   companyId: 0,
 *   competencia: '2025-01',
 * })
 */
export function useUrlState<T extends Record<string, Primitives>>(
    defaults: T,
    options?: { replace?: boolean },
): [T, (updates: Partial<T>) => void] {
    const [searchParams, setSearchParams] = useSearchParams()
    const replace = options?.replace ?? true

    const state = Object.fromEntries(
        Object.entries(defaults).map(([key, defaultVal]) => {
            const raw = searchParams.get(key)
            if (raw === null) return [key, defaultVal]
            if (typeof defaultVal === 'number') return [key, Number(raw)]
            if (typeof defaultVal === 'boolean') return [key, raw === 'true']
            return [key, raw]
        }),
    ) as T

    const setState = useCallback(
        (updates: Partial<T>) => {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    for (const [key, val] of Object.entries(updates)) {
                        if (val === null || val === undefined) next.delete(key)
                        else next.set(key, String(val))
                    }
                    return next
                },
                { replace },
            )
        },
        [setSearchParams, replace],
    )

    return [state, setState]
}
